/**
 * WCAG 2.2 AA for the pay-scale screens in dark mode (HF3).
 *
 * The app's primary colour is the company brand colour, which is often dark (#1A3C8F). On the dark
 * background, text and outlined buttons, outlined chips and focused field labels in that colour fall below 4.5:1. Inside
 * these screens only, those use a lightened brand colour in dark mode; contained buttons and
 * light mode are untouched.
 */
import type { ReactNode } from 'react';
import { lighten, ThemeProvider, type Theme } from '@mui/material/styles';

type Props = { ownerState: { variant?: string; color?: string }; theme: Theme };
type StyleFn = (p: Props) => Record<string, unknown>;
type AnyOverrides = Record<string, unknown> | undefined;

function darkSafePrimary(theme: Theme): string {
  return theme.palette.mode === 'dark' ? lighten(theme.palette.primary.main, 0.6) : theme.palette.primary.main;
}

/** The outer theme's own `root` override (object or function), resolved for these props. */
function resolve(base: unknown, p: Props): Record<string, unknown> {
  if (typeof base === 'function') return (base as StyleFn)(p) ?? {};
  return (base as Record<string, unknown>) ?? {};
}

/** Ours over theirs; a nested selector both define (e.g. '&.Mui-focused') is merged, not replaced. */
function merge(a: Record<string, unknown>, b: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = { ...a };
  for (const [k, v] of Object.entries(b)) {
    const prev = out[k];
    out[k] = prev && typeof prev === 'object' && v && typeof v === 'object'
      ? { ...(prev as Record<string, unknown>), ...(v as Record<string, unknown>) }
      : v;
  }
  return out;
}

/**
 * Adds ours ON TOP of the app's own `root` override for a component (never replaces it), so the
 * app's button / chip / input styling stays and only the colour changes.
 */
function withRoot(outer: Theme, name: string, mine: (p: Props) => Record<string, unknown>) {
  const comps = (outer.components ?? {}) as Record<string, { styleOverrides?: AnyOverrides } | undefined>;
  const comp = comps[name] ?? {};
  const overrides = (comp.styleOverrides ?? {}) as Record<string, unknown>;
  const base = overrides.root;
  return {
    ...comp,
    styleOverrides: { ...overrides, root: (p: Props) => merge(resolve(base, p), mine(p)) },
  };
}

const scoped = (outer: Theme): Theme => {
  if (outer.palette.mode !== 'dark') return outer;
  const fg = darkSafePrimary(outer);
  const primaryNonContained = (p: Props) => p.ownerState.variant !== 'contained' && (p.ownerState.color ?? 'primary') === 'primary';
  return {
    ...outer,
    components: {
      ...outer.components,
      MuiButton: withRoot(outer, 'MuiButton', (p) => (primaryNonContained(p) ? { color: fg, borderColor: fg } : {})),
      MuiChip: withRoot(outer, 'MuiChip', (p) =>
        p.ownerState.variant === 'outlined' && p.ownerState.color === 'primary' ? { color: fg, borderColor: fg } : {}),
      MuiInputLabel: withRoot(outer, 'MuiInputLabel', () => ({ '&.Mui-focused': { color: fg } })),
      MuiOutlinedInput: withRoot(outer, 'MuiOutlinedInput', () => ({ '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: fg } })),
    } as Theme['components'],
  };
};

export default function PayScalesThemeScope({ children }: { children: ReactNode }) {
  return <ThemeProvider theme={scoped}>{children}</ThemeProvider>;
}
